import { useCallback, useEffect, useRef, useState } from "react";
import getScrollParent from "./getScrollParent";

const FIRST_PAGE = { cursor: null, page: 1 };

/**
 * Cursor-or-page infinite loader with an IntersectionObserver sentinel.
 *
 * `fetchPage({ cursor, page })` resolves to `{ items, nextCursor, hasMore }`. When the API
 * returns no `nextCursor`, the hook falls back to page numbers. Items are de-duplicated by
 * `getKey`, and only one request is ever in flight. Changing `resetKey` starts over.
 */
export default function useInfiniteFeed({ fetchPage, getKey, resetKey, rootMargin = "800px 0px" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchPageRef = useRef(fetchPage);
  const getKeyRef = useRef(getKey);
  fetchPageRef.current = fetchPage;
  getKeyRef.current = getKey;

  const nextRef = useRef(FIRST_PAGE);
  const seenRef = useRef(new Set());
  const inFlightRef = useRef(false);
  const hasMoreRef = useRef(true);
  const generationRef = useRef(0);
  const intersectingRef = useRef(false);
  const observerRef = useRef(null);

  const loadMore = useCallback(async () => {
    if (inFlightRef.current || !hasMoreRef.current) return;
    inFlightRef.current = true;
    const generation = generationRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPageRef.current(nextRef.current);
      if (generation !== generationRef.current) return;

      const fresh = (res?.items || []).filter((item) => {
        const key = getKeyRef.current(item);
        if (seenRef.current.has(key)) return false;
        seenRef.current.add(key);
        return true;
      });
      const more = Boolean(res?.hasMore);
      nextRef.current = res?.nextCursor
        ? { cursor: res.nextCursor, page: nextRef.current.page + 1 }
        : { cursor: null, page: nextRef.current.page + 1 };
      hasMoreRef.current = more;
      setHasMore(more);
      if (fresh.length) setItems((prev) => prev.concat(fresh));
    } catch (e) {
      if (generation === generationRef.current) setError(e);
    } finally {
      if (generation === generationRef.current) {
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    generationRef.current += 1;
    nextRef.current = FIRST_PAGE;
    seenRef.current = new Set();
    inFlightRef.current = false;
    hasMoreRef.current = true;
    setItems([]);
    setHasMore(true);
    setError(null);
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    reset();
  }, [resetKey, reset]);

  // A short page can leave the sentinel on screen, which won't re-fire the observer.
  useEffect(() => {
    if (!loading && !error && hasMore && intersectingRef.current) loadMore();
  }, [loading, error, hasMore, items.length, loadMore]);

  const sentinelRef = useCallback(
    (node) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      intersectingRef.current = false;
      if (!node) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          intersectingRef.current = entry.isIntersecting;
          if (entry.isIntersecting) loadMore();
        },
        { root: getScrollParent(node), rootMargin }
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [loadMore, rootMargin]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { items, loading, error, hasMore, loadMore, reset, sentinelRef };
}
